'use client';

import type { JobResult } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/JobResult';
import type { PremiseResult } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/PremiseResult';
import type { SearchResults } from '@dte/otw-azure-functions/src/common/interfaces/admin/search/SearchResults';
import { getPremiseAddress } from '@dte/otw-azure-functions/src/common/premises/data/attributes/address/premiseAddress';
import { filterBlank } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { isEmptyArray } from '@dte/otw-utils-core/src/objects/isEmpty';
import { lowerCaseValue } from '@dte/otw-utils-core/src/strings/lowerCaseValue';
import { stableId } from '@dte/otw-utils-core/src/strings/stableId';
import { titleCaseValue } from '@dte/otw-utils-core/src/strings/titleCaseValue';
import { upperCaseValue } from '@dte/otw-utils-core/src/strings/upperCaseValue';
import { faMagnifyingGlass, faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { ActionIcon, TextInput, VisuallyHidden } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { type ReactElement, type ReactNode, useCallback, useId, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { mutate } from 'swr';
import { AuthenticatedJsonContent } from '../../../app/(client)/(authenticated)/content/jsonGet/AuthenticatedJsonContent';
import { JobLink } from '../../../app/(client)/(authenticated)/jobs/(jobId)/jobLink/JobLink';
import { PremiseLink } from '../../../app/(client)/(authenticated)/premises/(premiseId)/premiseLink/PremiseLink';
import { PremiseMarker } from '../../../app/(client)/(authenticated)/premises/search/PremiseMarker';
import { AppAction } from '../../../contexts/TokenContext/Roles';
import { useIsAllowed } from '../../../contexts/TokenContext/useIsAllowed';
import { useTriggerOnPathChange } from '../../../hooks/usePathChanged';
import { FocusableContainer } from '../../FocusableContainer';
import { Icon } from '../../Spinner/Icon';
import { formatLongDateTime } from '../../Tables/formatShortDateTime';
import { getSearchURL } from './getSearchURL';
import styles from './SearchBar.module.scss';

function refresh(): void {
	const path = '/authenticated/data';
	void mutate((key) => typeof key === 'string' && key.includes(path), undefined, { revalidate: true });
}

interface SearchProps {
	queryInput: string;
	setQueryInput: (value: string) => void;
	show: boolean;
	loading?: boolean;
}

function SearchInput(props: SearchProps): ReactNode {
	const queryInput = props.queryInput ?? '';
	const setQueryInput = props.setQueryInput;
	const inputRef = useRef<HTMLInputElement>(null);

	const changeQueryInput = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setQueryInput(value);
		},
		[setQueryInput],
	);

	const clearQueryInput = useCallback(() => {
		setQueryInput('');
		inputRef.current?.focus();
	}, [setQueryInput]);

	let placeholder = 'Search for Premise or Job';
	if (queryInput) {
		placeholder = '';
	}

	const searchInputId = useId();

	const searchButton = (
		<ActionIcon
			name="search"
			type="submit"
			className={styles['iconButton']}
			title={props.loading ? 'Loading...' : 'Search'}
			aria-controls="searchResults"
			aria-expanded={Boolean(props.show)}
			disabled={props.loading}
		>
			<Icon icon={props.loading ? faSpinner : faMagnifyingGlass} spin={props.loading} />
		</ActionIcon>
	);

	const clearButton = queryInput ? (
		<ActionIcon className={styles['iconButton']} onClick={clearQueryInput} aria-label="Clear search criteria">
			<Icon icon={faXmark} />
		</ActionIcon>
	) : undefined;

	return (
		<>
			<VisuallyHidden>
				<label htmlFor={searchInputId}>Search</label>
			</VisuallyHidden>
			<TextInput
				ref={inputRef}
				id={searchInputId}
				leftSectionPointerEvents="none"
				leftSection={searchButton}
				rightSectionPointerEvents="auto"
				rightSection={clearButton}
				value={queryInput}
				onChange={changeQueryInput}
				placeholder={placeholder}
				autoCapitalize="off"
				autoCorrect="off"
				autoComplete="off"
				spellCheck={false}
				aria-controls="searchResults"
				aria-haspopup="menu"
				className={styles['searchInput']}
			/>
		</>
	);
}

interface SearchResultsProps {
	queryInput: string;
	searchResults: SearchResults | undefined;
	show?: boolean;
	premiseResult?: PremiseResult;
	jobResult?: JobResult;
	loading?: boolean;
}

function longestTokenMatch(value: string, query: string | undefined): string | undefined {
	const lowerValue = lowerCaseValue(value);
	if (!lowerValue) {
		return undefined;
	}

	const tokens = query?.split(' ') ?? [];
	let longestMatch: string | undefined;

	for (const token of tokens) {
		// Do case insensitive matching
		const lowerToken = lowerCaseValue(token);

		// Only looking for things that start with the token
		if (!lowerToken) {
			continue;
		}
		if (!lowerValue.startsWith(lowerToken)) {
			continue;
		}

		// First match
		if (!longestMatch) {
			longestMatch = token;
		}

		// This token is longer than previous matches
		if (token.length > longestMatch.length) {
			longestMatch = token;
		}
	}

	return longestMatch;
}

interface HighlightedPartsProps {
	value: string | undefined;
	query: string | undefined;
	titleize?: boolean;
}

function HighlightedParts(props: HighlightedPartsProps): ReactNode {
	let value: string | undefined = props.value;
	if (props.titleize) {
		value = titleCaseValue(value);
	}

	if (!value) {
		return [<span key={0}>&nbsp;</span>];
	}

	const query = props.query;

	const valueParts = value.split(' ');

	const keyCount: Record<string, number> = {};
	const highlightedParts: ReactElement[] = valueParts.map((part, index) => {
		const token = longestTokenMatch(part, query);
		const tokenLength = token?.length;

		let ending: ReactNode = ' ';
		if (index === valueParts.length - 1) {
			ending = null;
		}

		// Get a consistent key
		const baseKey = stableId(part);
		// Add an incrementing suffix to the key
		let currentCount = keyCount[baseKey] ?? 1;
		const key = `${baseKey}-${currentCount}`;
		currentCount++;
		keyCount[baseKey] = currentCount;

		// No tokens matched
		if (!tokenLength) {
			return (
				<span key={key}>
					{part}
					{ending}
				</span>
			);
		}

		// Got a match
		const before = part.substring(0, tokenLength);
		const after = part.substring(tokenLength);
		return (
			<span key={key}>
				<b>{before}</b>
				{after}
				{ending}
			</span>
		);
	});

	return highlightedParts;
}

function shouldShowLine2(searchResults: SearchResults | undefined): boolean {
	const premises = searchResults?.premises;
	if (!premises) {
		return false;
	}

	const showLine2 = premises.find((findValue) => getPremiseAddress(findValue?.premiseData)?.line2);
	return showLine2 !== undefined;
}

function Line2Cell(props: SearchResultsProps): ReactNode {
	const premiseResult = props.premiseResult;
	if (!premiseResult) {
		return null;
	}

	const showLine2 = shouldShowLine2(props.searchResults);
	if (!showLine2) {
		return null;
	}

	const address = getPremiseAddress(premiseResult?.premiseData);
	const queryInput = props.queryInput;
	return (
		<td>
			<PremiseLink premiseId={premiseResult.premiseId}>
				<HighlightedParts query={queryInput} value={address?.line2} titleize={true} />
			</PremiseLink>
		</td>
	);
}

function PremiseResultDisplay(props: SearchResultsProps): ReactNode {
	const premiseResult = props.premiseResult;
	const premiseData = premiseResult?.premiseData;
	const address = getPremiseAddress(premiseData);
	if (!address) {
		return null;
	}

	const queryInput = props.queryInput;

	return (
		<tr>
			<td className={styles['marker']}>
				<PremiseMarker premiseData={premiseData} size="small">
					&nbsp;
				</PremiseMarker>
			</td>
			<td>
				<PremiseLink premiseId={premiseResult?.premiseId}>
					<HighlightedParts query={queryInput} value={address.line1} />
				</PremiseLink>
			</td>
			<Line2Cell {...props} />
			<td>
				<HighlightedParts query={queryInput} value={address.city} />
			</td>
			<td>
				<HighlightedParts query={queryInput} value={address.state} />
			</td>
		</tr>
	);
}

function JobResultDisplay(props: SearchResultsProps): ReactNode {
	const jobResult = props.jobResult;
	const jobStatus = jobResult?.jobStatus;
	if (!jobStatus) {
		return null;
	}

	const jobDisplayId = upperCaseValue(jobStatus?.data?.jobDisplayId);
	const created = jobStatus?.data?.jobCreateTime;

	return (
		<tr>
			<td>
				<JobLink jobId={jobResult.jobId}>{jobDisplayId}</JobLink>
			</td>
			<td>{titleCaseValue(jobStatus?.data?.jobType)}</td>
			<td>{formatLongDateTime(created)}</td>
		</tr>
	);
}

function getJobSearchList(searchResults: SearchResults | undefined, queryInput: string): ReactNode {
	let jobs = searchResults?.jobs || [];
	// Just get the first 10 elements for display
	jobs = jobs.slice(0, 10);

	let jobOptions = jobs.map((job) => {
		return (
			<JobResultDisplay key={job.jobId} queryInput={queryInput} searchResults={searchResults} jobResult={job} />
		);
	});

	// Filter out anything with null values
	jobOptions = filterBlank(jobOptions);
	if (isEmptyArray(jobOptions)) {
		return null;
	}

	return (
		<>
			<legend>Jobs</legend>
			<table>
				<VisuallyHidden component={'thead'}>
					<tr>
						<th>Job</th>
						<th>Status</th>
						<th>Created Date</th>
						<th>Created Time</th>
						<th>Restored Date</th>
						<th>Restored Time</th>
					</tr>
				</VisuallyHidden>
				<tbody>{jobOptions}</tbody>
			</table>
		</>
	);
}

function getPremiseSearchList(searchResults: SearchResults | undefined, queryInput: string): ReactNode {
	let premises = searchResults?.premises || [];
	// Just get the first 10 elements for display
	premises = premises.slice(0, 10);

	let premiseOptions = premises.map((premise) => {
		return (
			<PremiseResultDisplay
				key={premise.premiseId}
				queryInput={queryInput}
				searchResults={searchResults}
				premiseResult={premise}
			/>
		);
	});

	// Filter out anything with null values
	premiseOptions = filterBlank(premiseOptions);
	if (isEmptyArray(premiseOptions)) {
		return null;
	}

	const showLine2 = shouldShowLine2(searchResults);
	let line1Header = <th>Address</th>;
	let line2Header: ReactNode = null;
	if (showLine2) {
		line1Header = <th>Address Line 1</th>;
		line2Header = <th>Address Line 2</th>;
	}

	return (
		<>
			<legend>Premises</legend>
			<table>
				<VisuallyHidden component={'thead'}>
					<tr>
						<th>Status</th>
						{line1Header}
						{line2Header}
						<th>City</th>
						<th>State</th>
					</tr>
				</VisuallyHidden>
				<tbody>{premiseOptions}</tbody>
			</table>
		</>
	);
}

function SearchResultsContent(props: SearchResultsProps): ReactNode {
	const queryInput = props.queryInput;
	const searchResults = props.searchResults;

	const premiseListContent = getPremiseSearchList(searchResults, queryInput);

	if (premiseListContent !== null) {
		return premiseListContent;
	}

	const jobListContent = getJobSearchList(searchResults, queryInput);
	if (jobListContent !== null) {
		return jobListContent;
	}

	const loading = props.loading;
	if (loading) {
		return <>Loading...</>;
	}

	if (queryInput) {
		return <>No matches</>;
	}

	return (
		<>
			Enter an address or id to search:
			<ul>
				<li>Premise</li>
				<li>Call</li>
				<li>Job</li>
				<li>Order</li>
				<li>Meter</li>
			</ul>
		</>
	);
}

function SearchResultsSelection(props: SearchResultsProps): ReactNode {
	const show = props.show;

	const domId = stableId('searchResults');

	let content: ReactNode = null;
	if (show) {
		content = (
			<div className={styles['content']}>
				<SearchResultsContent {...props} />
			</div>
		);
	}

	return (
		<div id={domId} className={styles['selectionList']} role="menu" aria-roledescription="Search Results">
			{content}
		</div>
	);
}

export function SearchBar(): ReactNode {
	const [queryInput, setQueryInput] = useState<string>('');
	const hasAccess = useIsAllowed(AppAction.SearchOrders);

	const [debouncedQueryInput] = useDebouncedValue(queryInput, 100);
	const url = getSearchURL(debouncedQueryInput);

	const [searchResults, setSearchResults] = useState<SearchResults>();
	const [loading, setLoading] = useState(false);

	const [opened, { open, close }] = useDisclosure(false);

	// Hide the results if we select something
	const changePath = useCallback(() => {
		// Clear the query
		setQueryInput('');

		// Hide the overlay
		close();
	}, [close]);
	useTriggerOnPathChange(changePath);

	const search = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			if (!loading) {
				// On manually pressing search, clear caches and refresh
				refresh();
			}
			// Call prevent default becase we don't want to submit the form and cause a page refresh
			event.preventDefault();
		},
		[loading],
	);

	// Hide search widget unless we know that the user has access
	if (!hasAccess) {
		return null;
	}

	return (
		<Form onSubmit={search}>
			<FocusableContainer onFocus={open} onBlur={close} className={styles['inputColumn']}>
				<SearchInput queryInput={queryInput} setQueryInput={setQueryInput} show={opened} loading={loading} />
				<SearchResultsSelection
					queryInput={queryInput}
					searchResults={searchResults}
					loading={loading}
					show={opened}
				/>
			</FocusableContainer>
			<AuthenticatedJsonContent
				url={url}
				setData={setSearchResults}
				setLoading={setLoading}
				loadingContent={null}
			/>
		</Form>
	);
}
